import {v5 as uuidv5} from 'uuid'
import { COUNTRY_CODES } from './constants/globalConstants'
import { Constants } from './constants/commonConstants'

// Check if a value is an object
export const isObject = value => value !== null && typeof value === 'object'

export const padZero = val => {
  return ('' + val).padStart(2, '0')
}

export function getColorTag(currentStatus, shouldApplyColor = true) {
  const errorStatuses = ['cancelled', 'danger', 'processing_failure']
  const successStatuses = ['success', 'completed', 'approved']

  if (shouldApplyColor) {
    if (errorStatuses.includes(currentStatus?.toLocaleLowerCase())) {
      return 'text-red-300 font-bold'
    } else if (successStatuses.includes(currentStatus?.toLocaleLowerCase())) {
      return 'text-green-300 font-bold'
    }
  } else {
    return ''
  }
}

export function getFormattedDate(dateString, timeCheck = false, separator = '/', format = 'dd-mm-yyyy') {
  if (!dateString || dateString == undefined) return '-'
  const dateOnly = dateString?.split('T')[0] ?? dateString
  const date = new Date(dateOnly)
  const locale = navigator.language

  const dateParts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type === 'day') acc.day = part.value
      if (part.type === 'month') acc.month = part.value
      if (part.type === 'year') acc.year = part.value
      return acc
    }, {})

  const {day = '', month = '', year = ''} = dateParts

  const formatMapping = {
    'dd-mm-yyyy': `${day}${separator}${month}${separator}${year}`,
    'yyyy-dd-mm': `${year}${separator}${day}${separator}${month}`,
    'yyyy-mm-dd': `${year}${separator}${month}${separator}${day}`,
  }

  let formattedDateString = formatMapping[format] || formatMapping['dd-mm-yyyy']

  if (timeCheck) {
    formattedDateString += ` ${new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(date)}`
  }
  return formattedDateString
}

export const getSelectedRowData = (rowSelection = [], records = []) => {
  if (!Array.isArray(rowSelection)) {
    return Object.keys(rowSelection)?.length ? records.filter(record => record && rowSelection[record.id]) : []
  }

  return rowSelection?.map(selection => (selection?.index != null && records ? records[selection.index] : null))
}

export const getFormattedDateString = date => {
  if (date) {
    const formattedDate = new Date(date)
    return formattedDate.toISOString()
  } else {
    return ''
  }
}

export const convertToLowerCase = content => content?.toLowerCase() ?? content

export const copyToClipboard = value => {
  navigator.clipboard.writeText(value).catch(err => {
    console.error('Failed to copy text:', err)
  })
}

export const getISOTimeStringForReview = (reqDate, isEndDate = false, isStartDate = false) => {
  if (!reqDate) return
  const date = new Date(reqDate)
  if (isStartDate) {
    date?.setHours(0, 0, 0)
  }
  if (isEndDate) {
    date?.setHours(23, 59, 59)
  }
  return date.toISOString()
}

export const getISOTimeString = (reqDate, isEndDate = false, isStartDate = false) => {
  if (!reqDate) return
  let date = new Date(reqDate)
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  return convertToIsoString(date, isStartDate, isEndDate)
}

export const convertToIsoString = (date,isStartDate,isEndDate)=>{
    if (isStartDate) {
    date?.setHours(0, 0, 0, 0)
  }
  if (isEndDate) {
    date?.setHours(23, 59, 59, 999)
  }
  return date.toISOString()
}

export const getDateFromISOString = (isoString) => {
  if (!isoString) return;

  const date = new Date(isoString);

  // Extract in local time
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const formattedDate = `${month}/${day}/${year}`;
  return formattedDate;
};


export const replaceExrtaSpaces = input => {
  if (typeof input !== 'string') {
    return input != null ? String(input) : ''
  }
  return input
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const getUUID = (str = 'sales-target', namespace = uuidv5.DNS) => {
  return uuidv5(str?.toUpperCase(), namespace)
}

export const formatUTCTimestamp = () => {
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  const day = String(now.getUTCDate()).padStart(2, '0')
  const hours = String(now.getUTCHours()).padStart(2, '0')
  const minutes = String(now.getUTCMinutes()).padStart(2, '0')
  const seconds = String(now.getUTCSeconds()).padStart(2, '0')
  return `${year}${month}${day}_${hours}${minutes}${seconds}`
}


export const createValidationFunction = (regex, errorMessage) => (val) => {
  if (!regex.test(val)) {
    return errorMessage;
  }
};

/**
 * Checks if a value is empty, treating 0 as a valid value
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is empty, false otherwise
 */
export const isEmptyValue = (value) => {
  return !value && value !== 0;
};
/**
 * Get display currency based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency code (e.g., 'EGP', 'MXN')
 */
export const getDisplayCurrency = (countryCode) => {
  if (countryCode === COUNTRY_CODES.EGYPT) {
    return 'EGP'
  }
  return 'MXN' // Default currency
}

// Currency map for country codes
export const currencyMap = {
  MX: 'MXN',
  SA: 'SAR',
  EG: 'EGP',
  PE: 'PEN',
  EC: 'USD',
  CO: 'COP',
  AR: 'ARS',
}

// Currency map with full names for display
export const currencyMapWithNames = {
  MX: 'MXN (Mexican Peso)',
  SA: 'SAR (Saudi Riyal)',
  EG: 'EGP (Egyptian Pound)',
  PE: 'PEN (Peruvian Sol)',
  EC: 'USD (US Dollar)',
  CO: 'COP (Colombian Peso)',
  AR: 'ARS (Argentine Peso)',
}

/**
 * Get currency symbol based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency symbol (e.g., 'EGP', 'MXN')
 */
export const getCurrencySymbol = (countryCode) => {
  return currencyMap[countryCode] || '$'
}

/**
 * Get currency symbol with full name based on country code
 * @param {string} countryCode - The country code (e.g., 'EG', 'MX')
 * @returns {string} - Currency symbol with name (e.g., 'EGP (Egyptian Pound)', 'MXN (Mexican Peso)')
 */
export const getCurrencySymbolWithName = (countryCode) => {
  return currencyMapWithNames[countryCode] || '$'
}

export const isPublished = (status) => {
  return status === Constants.Published || status === 'PUBLISHED';
}
